import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, Palette, Kanban, Gift, Settings as SettingsIcon, LogOut, CreditCard, MessageSquare, ArrowLeftFromLine, Inbox, Sparkles, Receipt } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const nav = [
  { to: "/agency", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/agency/clients", icon: Users, label: "Tenants" },
  { to: "/agency/contracts", icon: FileText, label: "Contracts" },
  { to: "/agency/invoices", icon: Receipt, label: "Invoices" },
  { to: "/agency/brand-hub", icon: Palette, label: "Brand Hub" },
  { to: "/agency/roadmap", icon: Kanban, label: "Roadmap" },
  { to: "/agency/leads", icon: Inbox, label: "Leads" },
  { to: "/agency/referrals", icon: Gift, label: "Referrals" },
  { to: "/agency/public-page", icon: Sparkles, label: "Public Page" },
  { to: "/agency/subscription", icon: CreditCard, label: "Subscription" },
  { to: "/agency/feedback", icon: MessageSquare, label: "Feedback" },
  { to: "/agency/settings", icon: SettingsIcon, label: "Settings" },
];

export default function AgencyLayout() {
  const { agency, logout } = useAuth();
  const navigate = useNavigate();
  const impersonated = Boolean(agency?.impersonated_by);

  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate(impersonated ? "/admin/login" : "/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {impersonated && (
        <div className="bg-amber-500 text-white px-6 py-2 flex items-center justify-between text-sm font-medium" data-testid="impersonation-banner">
          <span>🛡️ You're viewing as <strong>{agency.name}</strong> (impersonation by {agency.impersonated_by})</span>
          <button onClick={onLogout} className="inline-flex items-center gap-1 hover:bg-amber-600 rounded-full px-3 py-1" data-testid="exit-impersonation-button">
            <ArrowLeftFromLine className="w-3.5 h-3.5" /> Back to admin
          </button>
        </div>
      )}
      <div className="flex flex-1 min-h-0">
        <aside className="w-64 shrink-0 border-r border-slate-200 bg-white p-6 flex flex-col" data-testid="agency-sidebar">
          <div className="mb-10 flex items-center gap-2">
            <img src="/logo-mark.png" alt="Foliosy" className="h-9 w-9 object-contain shrink-0" />
            <div className="min-w-0">
              <p className="font-bold tracking-tight truncate text-sm" data-testid="sidebar-agency-name">{agency?.name}</p>
              <p className="text-xs text-slate-500 truncate font-mono">{agency?.subdomain}</p>
            </div>
          </div>
          <nav className="flex flex-col gap-1">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <item.icon className="w-4 h-4" /> {item.label}
              </NavLink>
            ))}
          </nav>
          <button
            onClick={onLogout}
            className="mt-auto flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </aside>
        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
